import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import { SectionIntro, StatStrip, DeepCard, BulletGrid, FAQBlock, ResearchTable, LinkPills, PillList } from "../components/ContentBlocks";

const nuclearRows = [
  ["Inspection", "Enter zones where radiation, debris, heat or access constraints make direct human inspection unsafe", "Cameras, radiation sensors, thermal cameras, LiDAR, lights and rugged communication"],
  ["Mapping", "Build a readable view of rooms, pipes, vessels, corridors and blocked passages", "3D LiDAR, visual odometry, IMU, radiation overlays and operator annotation"],
  ["Manipulation", "Move valves, collect samples, handle tools or position sensors from a safe distance", "Manipulator arms, grippers, force feedback, tool changers and teleoperation"],
  ["Decommissioning support", "Assist dismantling, characterization and waste handling at a safe educational level", "Tracked bases, mobile manipulators, shielding aware planning and remote supervision"],
  ["Reporting", "Turn robot observations into decisions for engineers and safety teams", "Video logs, sensor logs, radiation maps, timestamps, task notes and maintenance records"]
];

const platformCards = [
  {
    title: "Tracked nuclear robots",
    description: "Tracked robots are common in hazardous facilities because tracks can handle debris, stairs, rough floors and low clearance better than many wheeled systems. The tradeoff is speed, turning friction, battery load and maintenance."
  },
  {
    title: "Manipulator arms",
    description: "Manipulator arms allow operators to move sensors, collect samples, open panels or use tools without standing near the hazard. Precision depends on cameras, force feedback, latency and the arm payload limit."
  },
  {
    title: "Radiation hardened cameras",
    description: "Nuclear robots need cameras and lights that can keep working in harsh areas. Image quality can still degrade because of radiation, dust, water, glare, smoke, darkness or narrow geometry."
  },
  {
    title: "LiDAR and radiation mapping",
    description: "LiDAR can help build maps while radiation sensors add risk context. The hard part is aligning sensor readings with position when GPS is unavailable and the environment is cluttered."
  },
  {
    title: "Custom robots for nuclear sites",
    description: "Custom robots for nuclear work are often built around one access problem pipe diameter, stair geometry, underwater visibility, radiation dose, tool reach, cable routing or retrieval path. The useful question is whether the robot can be recovered safely after a fault."
  }
];

const caseCards = [
  {
    title: "Fukushima Daiichi",
    description: "Robots have been used for monitoring, measurement, surveys, decontamination preparation and fuel debris removal support. The site shows why radiation, water, darkness, debris and unknown geometry make robot access difficult."
  },
  {
    title: "Sellafield",
    description: "Sellafield describes a customized Boston Dynamics Spot used for inspection, 3D mapping and radiological measurements. Its January 2026 case study distinguishes site deployments from a 2025 trial operated remotely from outside the licensed site."
  },
  {
    title: "Chernobyl",
    description: "Chernobyl remains a reminder that robots can help reduce human exposure, but radiation, rubble, dust and communication limits can quickly expose weak hardware and fragile control assumptions."
  },
  {
    title: "Active nuclear facilities",
    description: "Routine radiation surveys and inspection work can use mobile robots to collect readings, images and maps. Human specialists still need to validate results and plan safe actions."
  }
];

const limits = [
  "Autonomy is limited because maps may be incomplete, lighting can be poor and debris can block known paths.",
  "Communication loss is common in thick concrete, metal structures, underground spaces and shielded rooms.",
  "Radiation can damage electronics, cameras, batteries, connectors and memory over time.",
  "Dust, water, rubble, stairs and narrow passages can trap a robot or hide hazards from sensors.",
  "Teleoperation remains important because operators need judgment when a tool jams, visibility drops or the robot reaches an unknown area."
];

const faqs = [
  {
    question: "Why are robots used in nuclear environments?",
    answer: "Robots reduce direct human exposure during inspection, mapping, measurement, sampling, decontamination support and decommissioning tasks in dangerous or hard to access zones."
  },
  {
    question: "Are nuclear robots autonomous?",
    answer: "Some robots can navigate or map with partial autonomy, but many nuclear tasks still rely on teleoperation or supervised autonomy because the environment is uncertain and risk is high."
  },
  {
    question: "What sensors do nuclear robots use?",
    answer: "They can use RGB cameras, thermal cameras, LiDAR, IMU, radiation sensors, gas sensors, lights, microphones and sometimes force feedback through manipulator arms."
  },
  {
    question: "What makes decommissioning robots difficult?",
    answer: "Radiation, dust, debris, water, darkness, unknown geometry, communication loss, cable management, tool wear and recovery planning make decommissioning robotics difficult."
  },
  {
    question: "When do nuclear sites need custom robots?",
    answer: "Custom robots for nuclear sites are useful when a standard platform cannot fit the geometry, survive the dose, carry the tool, keep communication or be retrieved after a failure."
  }
];

export default function NuclearRobots() {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robot Types", to: "/robot-types" },
    { label: "Nuclear Robots", to: "/nuclear-robots" }
  ];
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Nuclear Robots and Robots for Decommissioning",
    description: "A practical guide to nuclear robots used for inspection, mapping, teleoperation and decommissioning support.",
    author: { "@type": "Organization", name: "TechniaHQRobot" }
  };

  return (
    <div className="article-page">
      <SEO
        title="Nuclear Robots and Decommissioning Robots"
        description="Learn how nuclear robots support inspection, mapping, teleoperation and decommissioning with tracked bases, arms, cameras and radiation sensors."
        path="/nuclear-robots"
        type="article"
        schema={[buildBreadcrumbSchema(breadcrumbs), schema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Hazardous environment robotics"
        title="Nuclear robots and robots for decommissioning"
        description="Nuclear robots are used where people should not stand directly in the hazard. They inspect, map, measure, manipulate and support decommissioning work through cameras, radiation sensors, LiDAR, tracked bases, arms and teleoperation."
      />

      <section className="site-shell py-10 sm:py-14">
        <ArticleAd placement="top" articleSlug="nuclear-robots" className="mb-8" />
        <StatStrip items={[
          { value: "inspection", label: "dangerous zones and unknown geometry" },
          { value: "teleoperation", label: "human judgment remains central" },
          { value: "radiation", label: "sensors and cameras must survive harsh areas" },
          { value: "decommissioning", label: "mapping, handling and reporting support" }
        ]} />
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Use cases"
          title="Why robots are used in nuclear environments"
          description="The goal is not to remove humans from responsibility. The goal is to keep specialists away from direct exposure while collecting better information and performing remote actions when the environment is unsafe."
        />
        <ResearchTable columns={["Use case", "What the robot does", "Typical hardware or data"]} rows={nuclearRows} />
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Robot platforms"
          title="Tracked bases, arms, hardened cameras and radiation sensors"
          description="Nuclear robot design is shaped by hazard, access and recovery. A robot that works in a clean lab may fail quickly in dust, water, rubble, radiation or weak communication."
        />
        <div className="grid gap-4 md:grid-cols-2">
          {platformCards.map((item) => <DeepCard key={item.title} title={item.title} description={item.description} />)}
        </div>
      </section>

      <section className="site-shell py-12 sm:py-16">
        <SectionIntro
          eyebrow="Examples"
          title="Fukushima, Sellafield, Chernobyl and active facilities"
          description="These examples show why nuclear robotics is not a simple autonomy story. The strongest systems combine rugged hardware, clear sensor data, operator control, safe procedures and honest limits."
        />
        <div className="grid gap-4 md:grid-cols-2">
          {caseCards.map((item) => <DeepCard key={item.title} title={item.title} description={item.description} />)}
        </div>
      </section>

      <section className="site-shell py-12 sm:py-16">
        <div className="soft-card bg-neutral-50/90 p-6 sm:p-8">
          <SectionIntro
            eyebrow="Autonomy limits"
            title="Why teleoperation and remote assistance remain important"
            description="Robots can help in nuclear work, but the environment often breaks assumptions. Operators still need clear video, sensor context, recovery options and safe decision authority."
            action={<LinkPills items={[{ label: "Inspection robots", to: "/robot-types/inspection-maintenance-robots" }, { label: "Robot sensors", to: "/robot-sensors" }, { label: "Robot components", to: "/robot-components" }, { label: "Robotics software", to: "/robotics-software" }]} />}
          />
          <PillList items={["tracked robots", "manipulator arms", "radiation sensors", "LiDAR", "teleoperation", "dust", "debris", "communication loss"]} />
          <div className="mt-6">
            <BulletGrid title="Current limits" items={limits} />
          </div>
        </div>
      </section>

      <section className="site-shell pb-10 sm:pb-14">
        <ArticleAd placement="bottom" articleSlug="nuclear-robots" />
      </section>

      <FAQBlock items={faqs} />
      <CTASection title="Follow TechniaHQRobot for hazardous environment robotics analysis" />
    </div>
  );
}
